import { useState } from 'react';
import type { FormEvent } from 'react';

import type { Fulfilment, FulfilmentInput } from '../api/jobs.ts';
import { localLabel } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ JOB-01 and QUO-01 at v1.946: collection, or delivery with an outbound and a return leg.
// Entered on the quote and copied to the job, so both screens show and edit it with this. A leg may
// wait until it is known; the database checks each falls inside the scheduled window.

/** JOB-01 at v1.986: each kind's name, the same in the panel, the jobs list and everywhere else. */
const LABELS: Record<Fulfilment['kind'], string> = {
  collection: 'Collection',
  delivery: 'Delivery',
  on_site: 'On site',
};

/** What each choice in the form says. */
const CHOICES: Record<Fulfilment['kind'], string> = {
  collection: 'Client collects',
  delivery: 'We deliver',
  on_site: 'On site, from our stock here',
};

export function fulfilmentLabel(kind: Fulfilment['kind']): string {
  return LABELS[kind];
}

export function FulfilmentPanel({
  fulfilment,
  editable,
  saving,
  error,
  onSave,
  onSiteOffered = false,
  changed = false,
}: {
  fulfilment: Fulfilment;
  editable: boolean;
  saving: boolean;
  error: Error | null;
  /** Resolves once saved; the panel then closes its form. */
  onSave: (input: FulfilmentInput) => Promise<unknown>;
  /** JOB-01 at v1.986: the venue keeps our stock, so on site may be chosen. */
  onSiteOffered?: boolean;
  /** JOB-01 at v1.986: a sent quote's on site became a collection; until sent again. */
  changed?: boolean;
}) {
  const [editing, setEditing] = useState(false);

  return (
    <section className="flex flex-col gap-3" aria-labelledby="fulfilment">
      <div className="flex items-center justify-between gap-4">
        <h2 id="fulfilment" className="section-title flex-1">
          {fulfilmentLabel(fulfilment.kind)}
        </h2>
        {editable && !editing ? (
          <Button variant="quiet" onClick={() => setEditing(true)}>
            Change
          </Button>
        ) : null}
      </div>
      {changed ? (
        <p role="status" className="notice">
          Changed to a collection: the new venue keeps none of our stock. The client was sent on
          site, so send the quote again.
        </p>
      ) : null}
      {editing ? (
        <FulfilmentForm
          fulfilment={fulfilment}
          saving={saving}
          error={error}
          onSiteOffered={onSiteOffered}
          onCancel={() => setEditing(false)}
          onSave={async (input) => {
            await onSave(input);
            setEditing(false);
          }}
        />
      ) : fulfilment.kind === 'collection' ? (
        <p className="text-ink-soft">The client collects and returns the gear.</p>
      ) : fulfilment.kind === 'on_site' ? (
        <p className="text-ink-soft">From our stock here to the room, in the same venue.</p>
      ) : (
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <Leg
            title="Delivered"
            local={fulfilment.outLocal ?? null}
            address={fulfilment.outAddress ?? null}
          />
          <Leg
            title="Picked up"
            local={fulfilment.returnLocal ?? null}
            address={fulfilment.returnAddress ?? null}
          />
        </dl>
      )}
    </section>
  );
}

function Leg({
  title,
  local,
  address,
}: {
  title: string;
  local: string | null;
  address: string | null;
}) {
  return (
    <div>
      <dt className="kicker">{title}</dt>
      <dd className="mt-1.5">
        <span className="font-extrabold">{local ? localLabel(local) : 'Time to be confirmed'}</span>
        <span className="block text-sm text-ink-soft">{address ?? 'Address to be confirmed'}</span>
      </dd>
    </div>
  );
}

function FulfilmentForm({
  fulfilment,
  saving,
  error,
  onSiteOffered,
  onCancel,
  onSave,
}: {
  fulfilment: Fulfilment;
  saving: boolean;
  error: Error | null;
  onSiteOffered: boolean;
  onCancel: () => void;
  onSave: (input: FulfilmentInput) => Promise<void>;
}) {
  const [kind, setKind] = useState(fulfilment.kind);
  const [outAt, setOutAt] = useState(fulfilment.outLocal ?? '');
  const [outAddress, setOutAddress] = useState(fulfilment.outAddress ?? '');
  const [returnAt, setReturnAt] = useState(fulfilment.returnLocal ?? '');
  const [returnAddress, setReturnAddress] = useState(fulfilment.returnAddress ?? '');

  function submit(event: FormEvent) {
    event.preventDefault();
    void onSave(
      kind === 'collection' || kind === 'on_site'
        ? { kind }
        : {
            kind,
            outAt: outAt || null,
            outAddress: outAddress.trim() || null,
            returnAt: returnAt || null,
            returnAddress: returnAddress.trim() || null,
          },
    ).catch(() => {
      // The mutation holds the error; it is shown below.
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 border-2 border-divider p-4">
      <fieldset className="flex gap-6">
        <legend className="sr-only">How the gear gets there</legend>
        {(
          [
            'collection',
            'delivery',
            ...(onSiteOffered || fulfilment.kind === 'on_site' ? (['on_site'] as const) : []),
          ] as const
        ).map((k) => (
          <label key={k} className="flex items-center gap-2">
            <input
              type="radio"
              name="fulfilment"
              checked={kind === k}
              onChange={() => setKind(k)}
            />
            {CHOICES[k]}
          </label>
        ))}
      </fieldset>
      {kind === 'delivery' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Delivered at"
            type="datetime-local"
            hint="Leave empty until it is known."
            value={outAt}
            onChange={(e) => setOutAt(e.currentTarget.value)}
          />
          <Field
            label="Delivered to"
            value={outAddress}
            onChange={(e) => setOutAddress(e.currentTarget.value)}
          />
          <Field
            label="Picked up at"
            type="datetime-local"
            value={returnAt}
            onChange={(e) => setReturnAt(e.currentTarget.value)}
          />
          <Field
            label="Picked up from"
            value={returnAddress}
            onChange={(e) => setReturnAddress(e.currentTarget.value)}
          />
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
